import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import {
  removeFetcherObjects,
  upsertFetcherInterface,
  upsertFetcherObjects,
} from "../../../../features/fetcherSettingsSlice";
import { setDisplayErrorSnackBar } from "../../../../features/userSlice";
import { FetcherObject } from "../helpers/fetcherSettingsDummyData";
import { objectsFor } from "../helpers/fetcherSettingsResolver";
import { formatDisplayDateTime, nowStamp } from "../helpers/formatters";
import { nextLocalId } from "../helpers/localIds";
import ObjectsPanel from "../ObjectsPanel";
import SaveChangesReviewModal, {
  FieldChange,
  ObjectChange,
} from "../SaveChangesReviewModal";

interface Props {
  open: boolean;
  interfaceId: string;
  mode: "view" | "edit";
  onClose: () => void;
  onSwitchToEdit: () => void;
  onDuplicate: (interfaceId: string) => void;
  onShowBrands: (interfaceId: string) => void;
  onOpenReport: (reportId: string) => void;
}

const isStagedId = (id: string) => id.startsWith("staged-");

const EditInterfaceModal = ({
  open,
  interfaceId,
  mode,
  onClose,
  onSwitchToEdit,
  onDuplicate,
  onShowBrands,
  onOpenReport,
}: Props) => {
  const dispatch = useDispatch();
  const { interfaces, reports, objects } = useSelector(
    (state: AppState) => state.fetcherSettings,
  );

  const backendInterface = interfaces.find((row) => row.id === interfaceId);

  const [name, setName] = useState("");
  const [dynamicVar, setDynamicVar] = useState("");
  const [stagedObjects, setStagedObjects] = useState<FetcherObject[]>([]);
  const [markedForDelete, setMarkedForDelete] = useState<string[]>([]);
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  const savedObjects = useMemo(
    () => objectsFor(objects, "interface_id", interfaceId),
    [objects, interfaceId],
  );

  useEffect(() => {
    if (!open || !backendInterface) return;
    setName(backendInterface.name);
    setDynamicVar(backendInterface.dvar ?? "");
    setStagedObjects(objectsFor(objects, "interface_id", backendInterface.id));
    setMarkedForDelete([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, interfaceId]);

  const isReadOnly = mode === "view";

  if (!backendInterface) return null;

  const clash = interfaces.find(
    (row) =>
      row.id !== backendInterface.id &&
      !!name.trim() &&
      row.name.toLowerCase() === name.trim().toLowerCase(),
  );

  const interfaceReports = reports.filter(
    (row) => row.iface === backendInterface.id,
  );

  const objectChanges: ObjectChange[] = [
    ...stagedObjects
      .filter((row) => isStagedId(row.id))
      .map<ObjectChange>((row) => ({
        kind: "ADD",
        fieldType: row.k,
        fieldValue: row.v,
      })),
    ...stagedObjects
      .filter((row) => {
        if (isStagedId(row.id)) return false;
        const saved = savedObjects.find((other) => other.id === row.id);
        return !!saved && (saved.k !== row.k || saved.v !== row.v);
      })
      .map<ObjectChange>((row) => ({
        kind: "UPDATE",
        fieldType: row.k,
        fieldValue: row.v,
      })),
    ...markedForDelete
      .map((id) => savedObjects.find((row) => row.id === id))
      .filter((row): row is FetcherObject => !!row)
      .map<ObjectChange>((row) => ({
        kind: "DELETE",
        fieldType: row.k,
        fieldValue: row.v,
      })),
  ];

  const fieldChanges: FieldChange[] = [];
  if (name !== backendInterface.name) {
    fieldChanges.push({
      label: "Name",
      from: backendInterface.name,
      to: name,
    });
  }
  if (dynamicVar !== (backendInterface.dvar ?? "")) {
    fieldChanges.push({
      label: "Common dynamic var",
      from: backendInterface.dvar ?? "",
      to: dynamicVar,
    });
  }

  const isDirty = fieldChanges.length > 0 || objectChanges.length > 0;

  const handleSave = () => {
    // TODO: PUT /aladdin_api/v1/data_collection/fetcher_interface/<shared_uid>
    // The body carries objects {create, update, delete} for the touched rows.
    let nextObjectId = Number(nextLocalId(objects));
    const written = stagedObjects
      .filter((row) => {
        if (isStagedId(row.id)) return true;
        const saved = savedObjects.find((other) => other.id === row.id);
        return !!saved && (saved.k !== row.k || saved.v !== row.v);
      })
      .map((row) => ({
        ...row,
        id: isStagedId(row.id) ? String(nextObjectId++) : row.id,
        lvl: "interface_id",
        raw: "interface_id",
        mid: backendInterface.id,
        mod: nowStamp(),
      }));

    if (markedForDelete.length) dispatch(removeFetcherObjects(markedForDelete));
    if (written.length) dispatch(upsertFetcherObjects(written));

    dispatch(
      upsertFetcherInterface({
        ...backendInterface,
        name: name.trim(),
        dvar: dynamicVar.trim(),
        mod: nowStamp(),
      }),
    );
    dispatch(
      setDisplayErrorSnackBar({
        message: `${name.trim()} saved.`,
        type: "success",
      }),
    );
    setIsReviewOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: "960px", maxWidth: "96vw" } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          {isReadOnly ? "View Interface" : "Edit Interface"}
        </DialogTitle>
        <Typography variant="body2" sx={{ px: 3, pb: 1.5, color: "#00000099" }}>
          {backendInterface.name} ({backendInterface.id}) · modified{" "}
          {formatDisplayDateTime(backendInterface.mod)}
        </Typography>
        <Divider />

        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            px: 3,
            py: 1.5,
            backgroundColor: "#FAFAFC",
            borderBottom: "1px solid #0000001F",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
              {backendInterface.brands}
            </Typography>
            {backendInterface.brands > 0 ? (
              <Link
                component="button"
                underline="always"
                color="secondary"
                sx={{ fontSize: "12px" }}
                onClick={() => onShowBrands(backendInterface.id)}
              >
                brands - view list
              </Link>
            ) : (
              <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
                brands
              </Typography>
            )}
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
              {interfaceReports.length}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
              reports
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
              {backendInterface.configs}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
              configs
            </Typography>
          </Box>
        </Stack>

        <DialogContent sx={{ px: 3, py: 2 }}>
          {clash && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <b>Blocked.</b> An interface named {clash.name} ({clash.id})
              already exists.
            </Alert>
          )}

          <Stack spacing={2.5}>
            <Typography
              sx={{
                fontSize: "11px",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                color: "#00000099",
                fontWeight: 700,
              }}
            >
              Identity
            </Typography>
            <TextField
              required
              fullWidth
              size="small"
              label="Interface Name"
              value={name}
              disabled={isReadOnly}
              onChange={(event) => setName(event.target.value)}
              color="secondary"
            />
            <TextField
              fullWidth
              size="small"
              label="Common Dynamic Var"
              placeholder="optional"
              value={dynamicVar}
              disabled={isReadOnly}
              onChange={(event) => setDynamicVar(event.target.value)}
              color="secondary"
            />
          </Stack>

          <ObjectsPanel
            ownLabel="This interface"
            ownObjects={stagedObjects}
            markedForDelete={markedForDelete}
            readOnly={isReadOnly}
            onAdd={(fieldType, fieldValue) =>
              setStagedObjects((previous) => [
                ...previous,
                {
                  id: `staged-${Date.now()}`,
                  lvl: "interface_id",
                  raw: "interface_id",
                  mid: backendInterface.id,
                  k: fieldType,
                  v: fieldValue,
                  ufr: "0",
                  mod: "",
                },
              ])
            }
            onEdit={(id, fieldType, fieldValue) =>
              setStagedObjects((previous) =>
                previous.map((row) =>
                  row.id === id ? { ...row, k: fieldType, v: fieldValue } : row,
                ),
              )
            }
            onMarkDelete={(id) => {
              if (isStagedId(id)) {
                setStagedObjects((previous) =>
                  previous.filter((row) => row.id !== id),
                );
                return;
              }
              setMarkedForDelete((previous) =>
                previous.includes(id) ? previous : [...previous, id],
              );
            }}
            onUndoDelete={(id) =>
              setMarkedForDelete((previous) =>
                previous.filter((rowId) => rowId !== id),
              )
            }
          />

          <Paper variant="outlined" sx={{ mt: 2 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                px: 2,
                py: 1.5,
                borderBottom: "1px solid #0000001F",
              }}
            >
              Reports on this interface
            </Typography>
            {interfaceReports.length === 0 ? (
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#00000099",
                  textAlign: "center",
                  py: 3,
                }}
              >
                No reports. Nothing can be collected from this interface.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Report</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Configs</TableCell>
                    <TableCell>Objects</TableCell>
                    <TableCell>Brands</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {interfaceReports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        <Link
                          component="button"
                          underline="always"
                          color="secondary"
                          onClick={() => onOpenReport(report.id)}
                        >
                          {report.name}
                        </Link>{" "}
                        ({report.id})
                      </TableCell>
                      <TableCell>{report.ctype}</TableCell>
                      <TableCell>{report.configs}</TableCell>
                      <TableCell>{report.objects}</TableCell>
                      <TableCell>{report.brands}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {isReadOnly ? (
            <>
              <Chip size="small" label="Read only" />
              <Box sx={{ flex: 1 }} />
              <Button color="secondary" onClick={onClose}>
                Close
              </Button>
              <Button
                color="secondary"
                onClick={() => onDuplicate(backendInterface.id)}
              >
                Duplicate
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={onSwitchToEdit}
              >
                Edit
              </Button>
            </>
          ) : (
            <>
              {isDirty && (
                <Typography sx={{ fontSize: "12.5px", color: "#B45309" }}>
                  ● unsaved changes
                  {markedForDelete.length > 0
                    ? ` · ${markedForDelete.length} object to delete`
                    : ""}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              {isDirty && (
                <Button
                  color="secondary"
                  onClick={() => {
                    setName(backendInterface.name);
                    setDynamicVar(backendInterface.dvar ?? "");
                    setStagedObjects(savedObjects);
                    setMarkedForDelete([]);
                  }}
                >
                  Discard
                </Button>
              )}
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={!isDirty || !!clash}
                onClick={() => setIsReviewOpen(true)}
              >
                Save changes
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <SaveChangesReviewModal
        open={isReviewOpen}
        entityLabel="Interface"
        itemName={backendInterface.name}
        fieldChanges={fieldChanges}
        objectChanges={objectChanges}
        affectedCount={backendInterface.brands}
        affectedDescription="brands bound to this interface"
        dependants={interfaceReports.map((report) => ({
          primary: report.name,
          secondary: `${report.brands} brands`,
        }))}
        onClose={() => setIsReviewOpen(false)}
        onConfirm={handleSave}
      />
    </>
  );
};

export default EditInterfaceModal;
